/**
 * Servicio principal de Wallet
 * Maneja creación, importación, login y sesión de wallets
 */

import { ethers } from 'ethers';
import config from '@/config';
import storageService, { WalletBasicInfo } from './storage.service';
import WalletManager from './wallet-manager';

// Variables de entorno
const INACTIVITY_TIMEOUT = parseInt(import.meta.env.VITE_INACTIVITY_TIMEOUT) || 15 * 60 * 1000;

/**
 * Servicio de Wallet
 */
export const walletService = {
  selectedNetwork: 'omdb',
  initialized: false,

  /**
   * Inicializa el servicio
   */
  initialize(): boolean {
    this.selectedNetwork = storageService.getSelectedNetwork();
    this.initialized = true;
    return true;
  },

  /**
   * Crea una nueva wallet
   */
  async createWallet(password: string, name = 'Primary Wallet'): Promise<{ address: string; mnemonic: string }> {
    try {
      // Crear wallet con ethers
      const wallet = ethers.Wallet.createRandom();

      if (!wallet.mnemonic?.phrase) {
        throw new Error('La wallet generada no contiene una frase mnemónica válida');
      }

      const mnemonic = wallet.mnemonic.phrase;
      console.log(`[Wallet] Nueva wallet creada con ${mnemonic.split(' ').length} palabras`);

      // Validar mnemonic
      if (!ethers.Mnemonic.isValidMnemonic(mnemonic)) {
        throw new Error('Error al generar la frase de recuperación');
      }

      // Datos de la wallet
      const walletData = {
        address: wallet.address,
        privateKey: wallet.privateKey,
        mnemonic,
        balance: 0,
        tokens: [],
        transactions: [],
        selectedNetwork: this.selectedNetwork,
        name,
      };

      // Encriptar y guardar
      await this.encryptAndStoreWallet(walletData, password, true);

      // Crear sesión
      await this.createSession(password);

      console.log(`[Wallet] Wallet creada: ${wallet.address}`);

      return {
        address: wallet.address,
        mnemonic,
      };
    } catch (error) {
      console.error('Error al crear wallet:', error);
      throw new Error('Failed to create wallet: ' + (error as Error).message);
    }
  },

  /**
   * Importa wallet desde clave privada
   */
  async importFromPrivateKey(privateKey: string, password: string): Promise<boolean> {
    try {
      let cleanPrivateKey = privateKey.trim();
      if (!cleanPrivateKey.startsWith('0x') && !cleanPrivateKey.startsWith('0X')) {
        cleanPrivateKey = `0x${cleanPrivateKey}`;
      }

      if (!/^0x[0-9a-fA-F]{64}$/.test(cleanPrivateKey)) {
        const hexBody = cleanPrivateKey.replace(/^0x/i, '');
        console.error(
          `[Wallet] Clave privada con formato inválido — longitud hex: ${hexBody.length} (se esperan 64), caracteres no-hex: ${/[^0-9a-fA-F]/.test(hexBody)}`
        );
        throw new Error('invalid private key');
      }

      const wallet = new ethers.Wallet(cleanPrivateKey);

      const walletData = {
        address: wallet.address,
        privateKey: wallet.privateKey,
        mnemonic: '', // Las wallets importadas por clave privada no tienen mnemonic
        balance: 0,
        tokens: [],
        transactions: [],
        selectedNetwork: this.selectedNetwork,
        name: 'Imported Wallet',
      };

      await this.encryptAndStoreWallet(walletData, password, true);
      await this.createSession(password);

      return true;
    } catch (error) {
      console.error('Error importing from private key:', error);
      return false;
    }
  },

  /**
   * Importa wallet desde mnemonic
   */
  async importFromMnemonic(mnemonic: string, password: string, name = 'Imported Wallet'): Promise<boolean> {
    try {
      const cleanMnemonic = mnemonic.trim().toLowerCase().replace(/\s+/g, ' ');
      console.log(`[Wallet] Importando wallet con ${cleanMnemonic.split(' ').length} palabras`);

      if (!ethers.Mnemonic.isValidMnemonic(cleanMnemonic)) {
        throw new Error('La frase de recuperación no es válida');
      }

      const hdWallet = ethers.HDNodeWallet.fromPhrase(cleanMnemonic);
      console.log(`[Wallet] Wallet creada: ${hdWallet.address}`);

      const walletData = {
        address: hdWallet.address,
        privateKey: hdWallet.privateKey,
        mnemonic: hdWallet.mnemonic?.phrase || cleanMnemonic,
        balance: 0,
        tokens: [],
        transactions: [],
        selectedNetwork: this.selectedNetwork,
        name,
      };

      await this.encryptAndStoreWallet(walletData, password, true);

      // Forzar que el selectedIndex apunte a la wallet recién importada
      const walletsData = storageService.getMultipleWalletsData();
      if (walletsData?.walletsBasicInfo) {
        const importedWalletIndex = walletsData.walletsBasicInfo.findIndex(w => w.address.toLowerCase() === walletData.address.toLowerCase());
        if (importedWalletIndex >= 0) {
          storageService.saveSelectedWalletIndex(importedWalletIndex);
        }
      }

      await this.createSession(password);

      // Validar que la wallet en memoria coincide con la que importamos
      const activeWallet = WalletManager.getInstance().getActiveWallet();
      if (activeWallet.address.toLowerCase() !== walletData.address.toLowerCase()) {
        console.error('[Wallet] ❌ Dirección en memoria no coincide con la importada — forzando corrección');
        WalletManager.getInstance().loadFromPrivateKey(walletData.privateKey, walletData.mnemonic);
      }

      return true;
    } catch (error) {
      console.error('Error importing from mnemonic:', error);

      if ((error as Error).message.includes('invalid mnemonic')) {
        throw new Error('La frase de recuperación no es válida');
      }

      throw error;
    }
  },

  /**
   * Encripta y guarda la wallet
   */
  async encryptAndStoreWallet(walletData: any, password: string, selectAfterSave = false): Promise<boolean> {
    try {
      if (!walletData || !password) {
        throw new Error('Wallet data and password are required');
      }

      console.log('[Wallet] Encriptando wallet');

      let encryptedWallet: string;

      // Si ya está encriptada, no encriptar de nuevo
      if (typeof walletData.privateKey === 'string' && walletData.privateKey.includes('"Crypto"')) {
        encryptedWallet = walletData.privateKey;
      } else {
        let wallet: ethers.Wallet | ethers.HDNodeWallet;
        if (walletData.mnemonic && walletData.mnemonic.trim() !== '') {
          wallet = ethers.HDNodeWallet.fromPhrase(walletData.mnemonic.trim());
        } else {
          wallet = new ethers.Wallet(walletData.privateKey);
        }
        encryptedWallet = await wallet.encrypt(password);
      }

      // Datos públicos (nunca exponer la clave)
      const walletStore = {
        address: walletData.address,
        balance: walletData.balance || 0,
        tokens: walletData.tokens || [],
        transactions: walletData.transactions || [],
        selectedNetwork: walletData.selectedNetwork || this.selectedNetwork,
        name: walletData.name || 'Primary Wallet',
        privateKey: encryptedWallet,
        mnemonic: encryptedWallet,
      };

      // Cargar wallets existentes desde storage (no partir de array vacío)
      const existingData = storageService.getMultipleWalletsData();
      const wallets: Array<{ address: string; name?: string; selectedNetwork?: string }> =
        existingData?.walletsBasicInfo ?? [];

      const existingIndex = wallets.findIndex((w) => w.address === walletData.address);

      if (existingIndex >= 0) {
        // Actualizar info básica de la wallet ya existente
        wallets[existingIndex] = {
          ...wallets[existingIndex],
          name: walletData.name,
          selectedNetwork: walletData.selectedNetwork,
        };
        storageService.saveWalletData(walletStore, wallets, existingIndex, !selectAfterSave);
      } else {
        // Agregar nueva wallet al listado
        wallets.push({
          address: walletData.address,
          name: walletData.name,
          selectedNetwork: walletData.selectedNetwork,
        });
        const newIndex = wallets.length - 1;
        storageService.saveWalletData(walletStore, wallets, newIndex, !selectAfterSave);
      }

      console.log('[Wallet] Wallet encriptada y guardada');
      return true;
    } catch (error) {
      console.error('[Wallet] Error al encriptar wallet:', error);
      throw new Error('Failed to encrypt and store wallet: ' + (error as Error).message);
    }
  },

  /**
   * Crea una sesión (SEGURA - sin claves en sessionStorage)
   */
  async createSession(password: string): Promise<string> {
    try {
      console.log('[Wallet] Creando sesión segura...');

      // ✅ NUEVO: Desbloquear wallet en memoria (NO en sessionStorage)
      const walletManager = WalletManager.getInstance();
      await walletManager.unlock(password);

      // Crear token de sesión (sin clave privada)
      const sessionToken = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem(config.storageKeys.session, sessionToken);

      // ❌ ELIMINADO: sessionStorage.setItem('wallet_privatekey', privateKey);

      // Tiempo de expiración
      const expiryTime = Date.now() + config.security.sessionDuration;
      sessionStorage.setItem('session_expiry', expiryTime.toString());
      sessionStorage.setItem('last_user_activity', Date.now().toString());

      console.log('[Wallet] Sesión creada');
      return sessionToken;
    } catch (error) {
      console.error('[Wallet] Error al crear sesión:', error);
      throw new Error('Failed to create session: ' + (error as Error).message);
    }
  },

  /**
   * Verifica si la contraseña es correcta
   */
  async verifyPassword(password: string): Promise<boolean> {
    try {
      await this.decryptWallet(password);
      return true;
    } catch (error) {
      console.error('Error al verificar contraseña:', error);
      return false;
    }
  },

  /**
   * Desencripta la wallet
   */
  async decryptWallet(password: string): Promise<any> {
    try {
      console.log('[Wallet] Desencriptando wallet');

      if (!password || typeof password !== 'string') {
        throw new Error('La contraseña es requerida');
      }

      // loadWalletData() respeta selectedIndex (a diferencia de la clave legacy).
      const walletData = storageService.loadWalletData();
      if (!walletData) {
        throw new Error('No se encontró una wallet guardada');
      }

      if (!walletData.privateKey) {
        throw new Error('Estructura de datos inválida');
      }

      let decryptedWallet: any;
      try {
        decryptedWallet = await ethers.Wallet.fromEncryptedJson(walletData.privateKey, password);
        console.log('[Wallet] Desencriptación exitosa');
      } catch (decryptError) {
        if ((decryptError as Error).message.includes('incorrect password')) {
          throw new Error('Contraseña incorrecta');
        }
        throw new Error('Error al desencriptar wallet: ' + (decryptError as Error).message);
      }

      // Guardar backup
      if (!localStorage.getItem('wallet_backup_data')) {
        localStorage.setItem('wallet_backup_data', JSON.stringify(walletData));
      }

      // Resetear contador de errores
      sessionStorage.removeItem('password_error_count');

      return {
        address: decryptedWallet.address,
        privateKey: decryptedWallet.privateKey,
        mnemonic: decryptedWallet.mnemonic?.phrase || '',
        balance: walletData.balance || 0,
        tokens: walletData.tokens || [],
        transactions: walletData.transactions || [],
        selectedNetwork: walletData.selectedNetwork || this.selectedNetwork,
        name: walletData.name || 'Primary Wallet',
      };
    } catch (error) {
      console.error('[Wallet] Error al desencriptar:', error);

      // Incrementar contador de errores
      const errorCount = parseInt(sessionStorage.getItem('password_error_count') || '0') + 1;
      sessionStorage.setItem('password_error_count', errorCount.toString());

      throw error;
    }
  },

  /**
   * Inicia sesión
   * Desencripta la wallet UNA sola vez y crea la sesión
   */
  async login(password: string): Promise<boolean> {
    try {
      console.log('[Wallet] Intentando login');

      // createSession llama a walletManager.unlock que desencripta la wallet.
      // Si la contraseña es incorrecta, unlock lanza un error.
      await this.createSession(password);

      localStorage.removeItem('wallet_requires_auth');
      sessionStorage.removeItem('session_forced_logout');
      sessionStorage.removeItem('password_error_count');

      console.log('[Wallet] Login exitoso');
      return true;
    } catch (error) {
      console.error('Error en login:', error);

      // Incrementar contador de errores de contraseña
      const errorCount = parseInt(sessionStorage.getItem('password_error_count') || '0') + 1;
      sessionStorage.setItem('password_error_count', errorCount.toString());

      return false;
    }
  },

  /**
   * Verifica si hay sesión activa
   */
  hasActiveSession(showLoginModal = false): boolean {
    const sessionToken = sessionStorage.getItem(config.storageKeys.session);
    if (!sessionToken) {
      if (showLoginModal) {
        this._handleInactiveSession('Sesión no encontrada');
      }
      return false;
    }

    const expiryTime = parseInt(sessionStorage.getItem('session_expiry') || '0');
    if (expiryTime < Date.now()) {
      sessionStorage.removeItem(config.storageKeys.session);
      sessionStorage.removeItem('session_expiry');

      if (showLoginModal) {
        this._handleInactiveSession('Sesión expirada');
      }
      return false;
    }

    const lastActivity = parseInt(sessionStorage.getItem('last_user_activity') || '0');
    const inactivityTime = Date.now() - lastActivity;

    if (lastActivity > 0 && inactivityTime > INACTIVITY_TIMEOUT) {
      sessionStorage.removeItem(config.storageKeys.session);
      sessionStorage.removeItem('session_expiry');

      if (showLoginModal) {
        this._handleInactiveSession('Inactividad detectada');
      }
      return false;
    }

    return true;
  },

  /**
   * Maneja sesión inactiva
   */
  _handleInactiveSession(reason: string): void {
    console.log(`[Wallet] No hay sesión activa (${reason})`);

    localStorage.setItem('wallet_requires_auth', 'true');
    localStorage.setItem('force_logout_redirect', 'true');

    this.logout();
  },

  /**
   * Cierra sesión (SEGURA - bloquea wallet en memoria)
   */
  logout(): void {
    console.log('[Wallet] Cerrando sesión...');

    // ✅ NUEVO: Bloquear wallet en memoria
    const walletManager = WalletManager.getInstance();
    walletManager.lock();

    // Limpiar sessionStorage
    sessionStorage.removeItem(config.storageKeys.session);
    sessionStorage.removeItem('session_expiry');
    sessionStorage.removeItem('last_user_activity');
    // ❌ YA NO NECESARIO: sessionStorage.removeItem('wallet_privatekey');
    sessionStorage.removeItem('temp_wallet_password');
    sessionStorage.removeItem('password_error_count');

    localStorage.setItem('wallet_requires_auth', 'true');
    sessionStorage.setItem('session_forced_logout', 'true');

    console.log('[Wallet] Sesión finalizada');

    document.dispatchEvent(
      new CustomEvent('session:logout', {
        detail: { timestamp: Date.now() },
      })
    );

    setTimeout(() => {
      localStorage.setItem('wallet_requires_auth', 'true');
      localStorage.setItem('force_logout_redirect', 'true');
      window.location.reload();
    }, 500);
  },

  /**
   * Obtiene la wallet activa desde memoria
   */
  getActiveWallet(): ethers.HDNodeWallet | ethers.Wallet {
    const walletManager = WalletManager.getInstance();
    return walletManager.getActiveWallet();
  },

  /**
   * Verifica si la wallet está desbloqueada
   */
  isWalletUnlocked(): boolean {
    const walletManager = WalletManager.getInstance();
    return walletManager.isUnlocked();
  },

  /**
   * Bloquea la wallet manualmente
   */
  lockWallet(): void {
    const walletManager = WalletManager.getInstance();
    walletManager.lock();
  },

  /**
   * Cambia la wallet activa
   */
  async switchWallet(index: number, password: string): Promise<boolean> {
    try {
      console.log(`[Wallet] Cambiando a wallet índice ${index}...`);

      // 1. Validar que la wallet existe
      const multiData = storageService.getMultipleWalletsData();
      if (!multiData || !multiData.walletsBasicInfo[index]) {
        throw new Error('La wallet seleccionada no existe');
      }

      // 2. Actualizar el índice en storage
      storageService.saveSelectedWalletIndex(index);

      // 3. Desbloquear la nueva wallet en memoria
      // Esto cargará automáticamente los datos del nuevo índice gracias a storageService.loadWalletData()
      await WalletManager.getInstance().unlock(password);

      console.log('[Wallet] Cambio de wallet exitoso');

      // 4. Notificar el cambio
      document.dispatchEvent(
        new CustomEvent('wallet:switched', {
          detail: {
            index,
            address: multiData.walletsBasicInfo[index].address,
          },
        })
      );

      return true;
    } catch (error) {
      console.error('[Wallet] Error al cambiar de wallet:', error);
      return false;
    }
  },

  /**
   * Lista todas las wallets; cae a la activa si aún no hay datos multiwallet.
   */
  getAllWallets(): { wallets: WalletBasicInfo[]; selectedIndex: number } {
    const data = storageService.getMultipleWalletsData();
    if (data?.walletsBasicInfo?.length) {
      return { wallets: data.walletsBasicInfo, selectedIndex: data.selectedIndex ?? 0 };
    }

    const address = WalletManager.getInstance().getActiveAddress();
    if (address) {
      return { wallets: [{ address, name: 'Primary Wallet' }], selectedIndex: 0 };
    }

    return { wallets: [], selectedIndex: 0 };
  },

  /**
   * Cambia de wallet reusando la contraseña de sesión; si no sirve, devuelve
   * 'wrong_password' para que la UI la pida puntualmente.
   */
  async switchWalletSeamless(index: number): Promise<'ok' | 'wrong_password'> {
    const password = WalletManager.getInstance().getSessionPassword();
    if (!password) return 'wrong_password';

    const success = await this.switchWallet(index, password);
    return success ? 'ok' : 'wrong_password';
  },

  /**
   * Agrega una wallet a la sesión ya desbloqueada, reusando su contraseña.
   */
  async addWallet(
    mode: 'create' | 'import-mnemonic' | 'import-key',
    data: string | undefined,
    name: string
  ): Promise<{ address: string; mnemonic?: string }> {
    const password = WalletManager.getInstance().getSessionPassword();
    if (!password) {
      throw new Error('Sesión no desbloqueada — no se puede agregar una wallet');
    }

    if (mode === 'create') {
      return this.createWallet(password, name);
    }

    if (mode === 'import-mnemonic') {
      if (!data) throw new Error('Frase de recuperación requerida');
      const success = await this.importFromMnemonic(data, password, name);
      const address = success ? WalletManager.getInstance().getActiveAddress() : null;
      if (!address) throw new Error('No se pudo importar la wallet');
      return { address };
    }

    // import-key
    if (!data) throw new Error('Clave privada requerida');
    const success = await this.importFromPrivateKey(data, password);
    const address = success ? WalletManager.getInstance().getActiveAddress() : null;
    if (!address) throw new Error('No se pudo importar la wallet');
    // importFromPrivateKey no acepta nombre — se renombra después si hace falta
    if (name) storageService.renameWallet(address, name);
    return { address };
  },

  /**
   * Elimina una wallet. No permite quedar en cero; si era la activa, cambia a la que quede.
   */
  async removeWallet(address: string): Promise<'ok' | 'last_wallet' | 'error'> {
    const data = storageService.getMultipleWalletsData();
    if (!data || data.walletsBasicInfo.length <= 1) {
      return 'last_wallet';
    }

    const wasActive = WalletManager.getInstance().getActiveAddress()?.toLowerCase() === address.toLowerCase();

    storageService.clearWalletData(true, address);

    if (wasActive) {
      const remaining = storageService.getMultipleWalletsData();
      const newIndex = remaining?.selectedIndex ?? 0;
      const result = await this.switchWalletSeamless(newIndex);
      if (result !== 'ok') return 'error';
    }

    return 'ok';
  },

  /**
   * Renombra una wallet del listado (no requiere que esté activa).
   */
  renameWallet(address: string, name: string): boolean {
    return storageService.renameWallet(address, name);
  },

  /**
   * Cambia la contraseña de todas las wallets a la vez (todo o nada).
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
    const walletsData = storageService.getMultipleWalletsData();
    if (!walletsData || walletsData.walletsBasicInfo.length === 0) {
      throw new Error('No hay wallets guardadas');
    }

    const decrypted: Record<string, ethers.HDNodeWallet | ethers.Wallet> = {};
    for (const info of walletsData.walletsBasicInfo) {
      const stored = walletsData.wallets[info.address];
      if (!stored?.privateKey) {
        throw new Error(`Datos inválidos para la wallet ${info.address}`);
      }
      try {
        decrypted[info.address] = await ethers.Wallet.fromEncryptedJson(stored.privateKey, oldPassword);
      } catch {
        throw new Error('Contraseña actual incorrecta');
      }
    }

    const updates: Record<string, any> = {};
    for (const [address, wallet] of Object.entries(decrypted)) {
      const encryptedWallet = await wallet.encrypt(newPassword);
      updates[address] = {
        ...walletsData.wallets[address],
        privateKey: encryptedWallet,
        mnemonic: encryptedWallet,
      };
    }
    storageService.updateWalletsEncryptedData(updates);

    // Re-cachea la sessionPassword — ya funciona porque se re-encriptó con newPassword.
    await WalletManager.getInstance().unlock(newPassword);

    return true;
  },

  /**
   * Extiende la sesión de la wallet
   */
  extendWalletSession(): void {
    const walletManager = WalletManager.getInstance();
    walletManager.extendSession();
  },
};

export default walletService;
